import { Renderer2 } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';


export const isSupportObjectFit = () => {
  return 'objectFit' in document.documentElement.style;
};

export function isNullOrUndefined(o: any): boolean {
  return typeof o === 'undefined' || o === null;
}

/** (瀏覽器端) 檢查設備是否為疑動觸控裝置 */ 
export const checkIsMobile = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/.test(window.navigator.userAgent) || checkIsIphone() || checkIsIpad();
};

/** (瀏覽器端) 檢查設備是否為 iphone */ 
export function checkIsIphone() {
  if ((window as any)['MSStream']) { return false; }
  if (window.navigator.platform) {
    return /iPhone/.test(window.navigator.platform);
  } else {
    return /iPhone/.test(window.navigator.userAgent);
  }
}

/** (瀏覽器端) 檢查設備是否為 ipad|ipod */ 
export function checkIsIpad() {
  if ((window as any)['MSStream']) { return false; }
  if (window.navigator.platform) {
    return /iPad|iPod/.test(window.navigator.platform) || (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
  } else {
    return /iPad|iPod/.test(window.navigator.userAgent) || /Mac/.test(window.navigator.userAgent)&& window.navigator.maxTouchPoints > 1;
  }
}

/** (瀏覽器端) 檢查設備是否為 Mac 電腦 */ 
export function checkIsMacDesktop() {
  if ((window as any)['MSStream']) { return false; }
  if (window.navigator.platform) {
    return (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints <= 1);
  } else {
    return /Mac/.test(window.navigator.userAgent)&& window.navigator.maxTouchPoints <= 1;
  }
}

/** (瀏覽器端) 檢查設備是否為 IOS 作業系統裝置 */ 
export function isIOS(): boolean {
  return checkIsIphone() || checkIsIpad() || checkIsMacDesktop();
}

/** 檢查是否為觸控螢幕裝置 */
export function checkIsTouchMobile(): boolean {
  let hasTouchScreen = false;
  const navigator: Navigator = window.navigator;
  if ('maxTouchPoints' in navigator) {
    hasTouchScreen = navigator.maxTouchPoints > 0;
  } else if ('msMaxTouchPoints' in navigator) {
    hasTouchScreen = (navigator as any)['msMaxTouchPoints'] > 0;
  } else {
    const mQ = window['matchMedia'] && window['matchMedia']('(pointer:coarse)');
    if (mQ && mQ.media === '(pointer:coarse)') {
      hasTouchScreen = !!mQ.matches;
    } else if ('orientation' in window) {
      hasTouchScreen = true; // deprecated, but good fallback
    } else {
      // Only as a last resort, fall back to user agent sniffing
      const UA = (navigator as Navigator).userAgent;
      hasTouchScreen = (
        /\b(BlackBerry|webOS|iPhone|IEMobile)\b/i.test(UA) ||
        /\b(Android|Windows Phone|iPad|iPod)\b/i.test(UA)
      );
    }
  }
  return hasTouchScreen;
}

/**
 * 動態載入 CSS 樣式表到文檔中。
 * 
 * @param document - 要插入 CSS 樣式表的文檔物件。
 * @param renderer - 用於與 DOM 互動的渲染器服務。
 * @param parentEl - 要將 CSS 樣式表插入到的父元素。
 * @param styleName - CSS 樣式表的唯一識別符。
 * @param path - CSS 樣式表的路徑。
 * @param crossOrigin - (可選) 是否要以跨源方式載入 CSS 樣式表。預設為 false。
 */
export function loadLazyCssFile(document: Document, renderer: Renderer2, parentEl: Element, styleName: string, path: string, refChild?: HTMLElement, crossOrigin = false) {
  if (document.getElementById(styleName)) { return; }

  // 在文檔中尋找 nonce
  let findNonce: string|null = null;
  const sts = document.querySelectorAll('style');
  for (let i = 0; i < sts.length && !findNonce; i++) {
    const sc = sts[i];
    const nonce = sc['nonce'] || sc.getAttribute('nonce');
    if (nonce) { findNonce = nonce; }
  }

  const styleElement = renderer.createElement('link');
  renderer.setAttribute(styleElement, 'id', `${styleName}`);
  if (findNonce) {
    renderer.setAttribute(styleElement, 'nonce', `${findNonce}`);
  }
  renderer.setAttribute(styleElement, 'href', `${path}`);
  renderer.setAttribute(styleElement, 'rel', `stylesheet`);
  if (crossOrigin) {
    renderer.setAttribute(styleElement, 'crossOrigin', `anonymous`);
    // FIXME: from moj frontend
    // renderer.setAttribute(styleElement, 'integrity', `sha256-${sha256(path)}`);
  }
  let body: HTMLBodyElement = parentEl.closest('body') as HTMLBodyElement;
  if (isNullOrUndefined(body)) {
    body = document.body as HTMLBodyElement;
  }
  const head: HTMLHeadElement = body.previousElementSibling as HTMLHeadElement;
  renderer.insertBefore(head, styleElement, refChild ? refChild : head.querySelector('link[rel="canonical"]'));
}

/**
 * 動態載入 JavaScript 文件到文檔中。
 * 
 * @param document - 將插入腳本的文檔對象。
 * @param renderer - 用於與 DOM 交互的渲染器服務。
 * @param scriptName - 腳本的唯一標識符。
 * @param path - 要加載的 JavaScript 文件的路徑。
 * @param useDefer - （可選）確定腳本是否應該使用 `defer` 屬性加載。
 * @param crossOrigin - （可選）確定腳本是否應該使用 `crossorigin` 屬性加載。默認為 `false`。
 */
export function loadLazyJsFile(document: Document, renderer: Renderer2, scriptName: string, path: string, useDefer?: boolean, crossOrigin = false) {
  if (document.getElementById(scriptName)) { return; }

  // 在文檔中尋找 nonce
  let findNonce: string|null = null;
  const scs = document.querySelectorAll('script');
  for (let i = 0; i < scs.length && !findNonce; i++) {
    const sc = scs[i];
    const nonce = sc['nonce'] || sc.getAttribute('nonce');
    if (nonce) { findNonce = nonce; }
  }

  const script = renderer.createElement('script');
  renderer.setAttribute(script, 'id', `${scriptName}`);
  renderer.setAttribute(script, 'type', `text/javascript`);
  if (findNonce) {
    renderer.setAttribute(script, 'nonce', `${findNonce}`);
  }
  renderer.setAttribute(script, 'src', `${path}`);
  renderer.setAttribute(script, 'async', ``);
  if (useDefer) {
    renderer.setAttribute(script, 'defer', ``);
  }
  if (crossOrigin) {
    renderer.setAttribute(script, 'crossOrigin', `anonymous`);
    // FIXME: from moj frontend
    // renderer.setAttribute(script, 'integrity', `sha256-${sha256(path)}`);
  }
  // 用 document 塞在 server 端不會有作用, 但沒關係這個在 client 端再執行就好
  // 用 parentEl.closest('body').previousSiblingsElement appendChild 也沒用 (但 insertBefore 沒試過)
  renderer.appendChild(document.head, script);
}

export function closestPolifill() {
  if (!Element.prototype.matches) {
    Element.prototype.matches =
      (Element.prototype as any).msMatchesSelector ||
      Element.prototype.webkitMatchesSelector;
  }

  if (!Element.prototype.closest) {
    Element.prototype.closest = function(s: any) {
      let el: any = this;

      do {
        if (typeof s === 'string' && Element.prototype.matches.call(el, s)) { return el; }
        else if (typeof s === 'object' && el === s) { return el; }
        el = el.parentElement || el.parentNode;
      } while (el !== null && el.nodeType === 1);
      return null;
    };
  }
}

/** 可餵入 HTMLElement做目標比對的 closest 方法 */
export const closest = (el: HTMLElement, s: string | HTMLElement) => {
  let target: HTMLElement|null|ParentNode = el;
  do {
    if (typeof s === 'string') {
      if (Element.prototype.matches.call(target, s)) { return target; }
    } else if (s instanceof HTMLElement) {
      if (target === s) { return target; }
    }
    target = target instanceof Node && (target['parentElement'] || target['parentNode']) || null;
  } while (target !== null && (target instanceof Node && target['nodeType'] && target['nodeType'] === 1));
  return null;
};


/** 將表格的查詢參數轉換成查 API 用的查詢參數 */
export function transferTableParams2HttpListParams(queryParam: any) {
  let params: {[param: string]: string | string[];} = {};
  if (queryParam.limit) {
    params['size'] = `${queryParam.limit}`;
  }
  if (queryParam.filter) {
    params['sfilterize'] = `${queryParam.filter}`;
  }
  if (queryParam.search) {
    params['search'] = `${queryParam.search}`;
  }
  if (queryParam.limit && (queryParam.offset || queryParam.offset === 0)) {
    params['page'] = `${queryParam.offset / queryParam.limit}`;
  }
  if (queryParam.sort && queryParam.order) {
    params['sort'] = `${queryParam.sort},${queryParam.order}`;
  }
  if (queryParam.query) {
    const query = JSON.parse(queryParam.query);
    const keys = Object.keys(query);
    if (keys.length !== 0) {
      for (const k of keys) {
        if (!isNullOrUndefined(query[k])) {
          params[k] = query[k];
        }
      }
    }
  }

  return params;
}

/** 將物件中未定義的值轉成 null */
export function transferObjNullVal(customSearchEntity: any) {
  for (let keys = Object.keys(customSearchEntity), i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (typeof customSearchEntity[key] === 'undefined') {
      customSearchEntity[key] = null;
    }
  }
}

/** 將物件中未定義或空值移除 */
export function rmObjNullorUndefinedVal(customSearchEntity: any) {
  for (let keys = Object.keys(customSearchEntity), i = 0; i < keys.length; i++) {
    const key = keys[i];
    if (customSearchEntity[key] === null || customSearchEntity[key] === undefined) {
      delete customSearchEntity[key];
    }
  }
}

/** 打亂陣列排序 */
export function randomData(data: any[]): any[] {
  // tslint:disable-next-line: only-arrow-functions
  data.sort(function() {
    return (0.5 - Math.random());
  });
  return data;
}

export function removeUrlQueryParam(url: string): string {
  let queryIndex = url.indexOf('?');
  if (queryIndex === -1) { queryIndex = url.indexOf(';'); }
  if (queryIndex > 0) {
    url = url.substring(0, queryIndex);
  }
  return url;
}

/**
 * 解析 URL 中的查詢參數，並將其轉換為一個對象。
 * 
 * 此函數可以處理兩種格式的查詢參數：
 * 1. 標準格式：以 '?' 開始，參數間用 '&' 分隔。
 * 2. 分號格式：以 ';' 開始，參數間用 '&' 分隔。
 * 
 * 注意：如果存在重複的參數名，後面的值會覆蓋前面的值。
 * 
 * @param {string} url - 要解析的 URL，通常是 location.href
 * @returns {Object} 包含所有查詢參數的對象。鍵是參數名，值是參數值。
 * 
 * @example
 * // 假設 url 是 "https://example.com?param1=value1&param2=value2"
 * const params = parseQueryParams(url);
 * // 結果: { param1: 'value1', param2: 'value2' }
 * 
 * @example
 * // 處理分號格式
 * const url = "https://example.com;param1=value1&param2=value2"
 * const params = parseQueryParams(url);
 * // 結果: { param1: 'value1', param2: 'value2' }
 */
export function parseQueryPqrams(url: string) {
  let queryParamSatrtIndex = url.indexOf('?');
  const queryParam: any = {};
  if (queryParamSatrtIndex !== -1) {
    const urlQueryParam = url.slice(queryParamSatrtIndex + 1, url.length);
    urlQueryParam.split('&').forEach(qps => {
      const a = qps.split('=');
      queryParam[a[0]] = a[1];
    });
    url = url.replace('?' + urlQueryParam, '');
  }

  queryParamSatrtIndex = url.indexOf(';');
  if (queryParamSatrtIndex !== -1) {
    const urlQueryParam = url.slice(queryParamSatrtIndex + 1, url.length);
    urlQueryParam.split('&').forEach(qps => {
      const a = qps.split('=');
      queryParam[a[0]] = a[1];
    });
  }

  return queryParam;
}

/**
 * 解析 JWT (JSON Web Token) 並返回其載荷（payload）部分。
 *
 * 注意：這個函數只解析 token，不驗證其簽名或有效性。
 * 
 * @param {string} accessToken - 完整的 JWT token 字符串。
 * @returns {Object} 解析後的 JWT payload 對象。
 * 
 * @example
 * const token = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c';
 * const payload = parseJWTtoken(token);
 * console.log(payload);
 * // 輸出: { sub: '1234567890', name: 'John Doe', iat: 1516239022 }
 * 
 * @throws {Error} 如果 token 格式不正確或解析失敗。
 */
export function parseJWTtoken(accessToken: string) {
  const base64Url = accessToken.split('.')[1];
  let base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  switch (base64.length % 4) {
    case 0: break;
    case 2: base64 += '=='; break;
    case 3: base64 += '='; break;
    default: break;
  }

  const jwtPayloadStr = decodeURIComponent(escape(atob(base64)));
  return JSON.parse(jwtPayloadStr);
}

export const fadeInOutAnimation = trigger('fadeInOut',
  [
    state('true', style({
      visibility: 'inherit',
      opacity: 1
    })),
    state('false', style({
      visibility: 'hidden',
      opacity: 0
    })),
    transition('* => *', animate('0.5s'))
  ]
);

/** 轉換貨幣字符成數字 */
export function currencyToNum(currency: string) {
  return Number(currency.toString().replace(/[^0-9.-]+/g,''));
}

/** 轉換數字成貨幣字符 */
export function numCurrency(num: number) {
  return Intl.NumberFormat('en-US').format(num);
}

/** 回傳範圍內的數值陣列 */
export function range(end: number, start = 0, step = 1, includeEnd = true): Array<number> {
  const size = (end - start) / step + (includeEnd ? 1 : 0);

  return Array.from(Array(size)).map((_, i) => start + i * step);
}

/**
 * Function that returns element rectangle & position (width, height, top, left)
 * @param element - Element which position & rectangle are returned
 */
export function getRectangle(element: HTMLElement) {
  const width = Math.max(element.offsetWidth, element.clientWidth, element.scrollWidth);
  const height = Math.max(element.offsetHeight, element.clientHeight, element.scrollHeight);

  let top = 0;
  let left = 0;

  do {
    top += element.offsetTop || 0;
    left += element.offsetLeft || 0;
    element = element.offsetParent as HTMLElement;
  } while(element);

  return { top, left, width, height };
}